/**
 * StayFinder homepage marketing image slots (S1, S4–S6, S8, S9).
 * STAGED — Editorial / REAL — Documentary; not listing inventory photos.
 * @see docs/design/STAYFINDER_IMAGE_AUTHORITY_SYSTEM_V1.md
 */

const PUBLIC_HOME = '/images/homepage';

/** @typedef {'unsplash' | 'pexels' | 'pixabay'} ImageSource */

/**
 * @typedef {Object} HomePageImageAsset
 * @property {string} slot Design slot id (S1, S4, …)
 * @property {string} id Source platform id
 * @property {ImageSource} platform
 * @property {string} file Local public path filename
 * @property {string} src Resolved public URL path
 * @property {string} photographer
 * @property {string} [photographerUrl]
 * @property {string} sourceUrl Canonical source page
 * @property {string} license
 * @property {string} [licenseUrl]
 * @property {string} imageClass Authority class label
 * @property {string} altKey i18n key for alt text
 * @property {string} [localCopyNote] Ops note when local file differs from approved source
 */

/** @type {Record<string, HomePageImageAsset>} */
export const HOME_PAGE_IMAGES = {
  hero: {
    slot: 'S1',
    id: 'TeFI-IHmFi8',
    platform: 'unsplash',
    file: 'hero-family-arrival.jpg',
    src: `${PUBLIC_HOME}/hero-family-arrival.jpg`,
    photographer: 'Kate Bezzubets',
    photographerUrl: 'https://unsplash.com/@k8_bzz',
    sourceUrl: 'https://unsplash.com/photos/TeFI-IHmFi8',
    license: 'Unsplash License',
    licenseUrl: 'https://unsplash.com/license',
    imageClass: 'STAGED — Editorial',
    altKey: 'home.images.hero_alt',
  },
  neighborhood: {
    slot: 'S4',
    id: '26756561',
    platform: 'pexels',
    file: 'neighborhood-cafe-sidewalk.jpg',
    src: `${PUBLIC_HOME}/neighborhood-cafe-sidewalk.jpg`,
    photographer: 'Burcu Elmas',
    photographerUrl: 'https://www.pexels.com/@burcu-elmas-1681130/',
    sourceUrl: 'https://www.pexels.com/photo/people-sitting-at-cafe-on-sidewalk-26756561/',
    license: 'Pexels License',
    licenseUrl: 'https://www.pexels.com/license/',
    imageClass: 'REAL — Documentary',
    altKey: 'home.images.neighborhood_alt',
  },
  apartment: {
    slot: 'S6',
    id: 'P6UfxsQLGzU',
    platform: 'unsplash',
    file: 'apartment-exterior-berlin.jpg',
    src: `${PUBLIC_HOME}/apartment-exterior-berlin.jpg`,
    photographer: 'Aimar Gallardo',
    photographerUrl: 'https://unsplash.com/@_piscolino_',
    sourceUrl: 'https://unsplash.com/photos/P6UfxsQLGzU',
    license: 'Unsplash License',
    licenseUrl: 'https://unsplash.com/license',
    imageClass: 'REAL — Documentary',
    altKey: 'home.images.apartment_alt',
  },
  familyHome: {
    slot: 'S6',
    id: '32122155',
    platform: 'pexels',
    file: 'family-home-germany.jpg',
    src: `${PUBLIC_HOME}/family-home-germany.jpg`,
    photographer: 'Christina & Peter',
    photographerUrl: 'https://www.pexels.com/@christina-peter-1763031647/',
    sourceUrl: 'https://www.pexels.com/photo/historic-german-house-with-stone-facade-32122155/',
    license: 'Pexels License',
    licenseUrl: 'https://www.pexels.com/license/',
    imageClass: 'REAL — Documentary',
    altKey: 'home.images.family_home_alt',
  },
  townhouse: {
    slot: 'S6',
    id: '489015',
    platform: 'pixabay',
    file: 'townhouse-dortmund.jpg',
    src: `${PUBLIC_HOME}/townhouse-dortmund.jpg`,
    photographer: 'Pixabay contributor',
    sourceUrl: 'https://pixabay.com/photos/phoenix-lake-city-houses-dortmund-489015/',
    license: 'Pixabay Content License',
    licenseUrl: 'https://pixabay.com/service/license/',
    imageClass: 'REAL — Documentary',
    altKey: 'home.images.townhouse_alt',
    localCopyNote:
      'Approved Pixabay 489015; automated CDN fetch returned 403. Local file is terraced row substitute (Unsplash 3WpvnLTfK7Q / Tanya Barrow) until manual Pixabay import.',
  },
  moveSettled: {
    slot: 'S8',
    id: '4569340',
    platform: 'pexels',
    file: 'move-settled-unpacking.jpg',
    src: `${PUBLIC_HOME}/move-settled-unpacking.jpg`,
    photographer: 'cottonbro studio',
    photographerUrl: 'https://www.pexels.com/@cottonbro/',
    sourceUrl: 'https://www.pexels.com/photo/family-unpacking-after-moving-4569340/',
    license: 'Pexels License',
    licenseUrl: 'https://www.pexels.com/license/',
    imageClass: 'STAGED — Editorial',
    altKey: 'home.images.move_settled_alt',
  },
  owner: {
    slot: 'S9',
    id: 'MdcJY5doCTo',
    platform: 'unsplash',
    file: 'owner-host-welcome.jpg',
    src: `${PUBLIC_HOME}/owner-host-welcome.jpg`,
    photographer: "Land O'Lakes, Inc.",
    photographerUrl: 'https://unsplash.com/@landolakesinc',
    sourceUrl: 'https://unsplash.com/photos/MdcJY5doCTo',
    license: 'Unsplash License',
    licenseUrl: 'https://unsplash.com/license',
    imageClass: 'STAGED — Editorial',
    altKey: 'home.images.owner_alt',
  },
};

/** Homepage slot order for steps section (housing journey). */
export const HOME_PAGE_STEP_IMAGES = [
  HOME_PAGE_IMAGES.apartment,
  HOME_PAGE_IMAGES.familyHome,
  HOME_PAGE_IMAGES.moveSettled,
];

export function getHomePageImageAttribution(image) {
  return {
    slot: image.slot,
    platform: image.platform,
    id: image.id,
    photographer: image.photographer,
    sourceUrl: image.sourceUrl,
    license: image.license,
    imageClass: image.imageClass,
    localCopyNote: image.localCopyNote || null,
  };
}

export const HOME_PAGE_IMAGE_ATTRIBUTION = Object.values(HOME_PAGE_IMAGES).map(getHomePageImageAttribution);
